import { useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import L from 'leaflet';
import { parseAvoidSegment } from '../../utils/avoidAreaHelpers';
import { getUsedProfileNames } from '../../utils/FleetHelpers';
import { colors } from '../../global/variables';

const { avoidArea } = colors;

const getProfileSegmentIds = (vehicleProfiles = []) =>
  vehicleProfiles
    .flatMap((profile) => profile?.avoid?.segments || [])
    .map((value) => parseAvoidSegment(value)?.id)
    .filter(Boolean);

const AvoidSegmentsOverlay = ({ map, tourPlanner }) => {
  const geometries = useSelector((state) => state.avoidSegmentGeometries);
  const draftAvoidSegments = useSelector((state) => state.draftAvoidSegments);
  const layersRef = useRef([]);

  const segmentIds = useMemo(() => {
    const allProfiles = tourPlanner?.vehicleProfiles || [];
    const profilesExcludingDraft =
      draftAvoidSegments !== null
        ? allProfiles.filter((_, i) => i !== draftAvoidSegments.profileIndex)
        : allProfiles;
    const usedProfileNames = getUsedProfileNames(tourPlanner?.vehicles);
    const filteredProfiles = profilesExcludingDraft.filter((profile) =>
      usedProfileNames.has(profile.name),
    );
    const saved = getProfileSegmentIds(filteredProfiles);
    const draft = draftAvoidSegments?.segmentIds || [];
    return [...saved, ...draft];
  }, [tourPlanner?.vehicleProfiles, tourPlanner?.vehicles, draftAvoidSegments]);

  useEffect(() => {
    if (!map) return undefined;

    layersRef.current.forEach((layer) => map.removeLayer(layer));
    layersRef.current = segmentIds
      .filter((id) => geometries[id])
      .map((id) =>
        L.polyline(geometries[id], { color: avoidArea, weight: 5, opacity: 0.85 })
          .bindTooltip(id, { sticky: true, direction: 'top', offset: [0, -8] })
          .addTo(map),
      );

    return () => {
      layersRef.current.forEach((layer) => map.removeLayer(layer));
      layersRef.current = [];
    };
  }, [map, segmentIds, geometries]);

  return null;
};

export default AvoidSegmentsOverlay;
